import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const employerFAQs = [
  {
    q: 'How quickly can WorkNation provide workers?',
    a: 'For most requests, WorkNation can provide qualified workers within 24-48 hours. For urgent construction or warehouse staffing needs, we often source candidates same-day depending on availability across British Columbia.',
  },
  {
    q: 'What industries does WorkNation specialize in?',
    a: 'We specialize in construction, warehouse and logistics, agriculture, transportation, and cold storage staffing across British Columbia. With 200+ workers placed, we have deep experience matching the right talent to the right role in each sector.',
  },
  {
    q: 'How does the temp-to-perm process work?',
    a: 'Our temp-to-perm program lets employers trial workers before committing to a permanent hire. Workers start on a temporary assignment, and if both parties are satisfied, the worker transitions to a full-time role with your company\u2014reducing hiring risk significantly.',
  },
  {
    q: 'Does WorkNation handle payroll and WCB compliance?',
    a: 'Yes. WorkNation handles all payroll administration, WCB (Workers Compensation Board) coverage, and WorkSafe BC compliance for every placed worker. This removes the administrative burden from employers completely.',
  },
  {
    q: 'What happens if a worker doesn\u2019t show up or meet expectations?',
    a: 'We stand behind our placements. If a worker fails to meet your expectations or doesn\u2019t show up, we will arrange a replacement as quickly as possible\u2014typically within hours\u2014at no additional cost to you.',
  },
  {
    q: 'Can you provide workers for short-term projects?',
    a: 'Absolutely. We offer daily, weekly, and project-based staffing. Whether you need workers for a single day or an entire construction season, WorkNation has the flexibility to meet your needs.',
  },
  {
    q: 'What areas in British Columbia does WorkNation serve?',
    a: 'WorkNation serves all of British Columbia including Metro Vancouver, Surrey, Burnaby, Richmond, Delta, Langley, Abbotsford, the Fraser Valley, and beyond. We are based in Vancouver and provide workforce solutions province-wide.',
  },
];

const jobSeekerFAQs = [
  {
    q: 'How do I register for work with WorkNation?',
    a: 'Visit our Job Seekers page and fill out the contact form, or email your resume directly to resumeworknation@gmail.com. Our team will review your qualifications and match you with available opportunities as quickly as possible.',
  },
  {
    q: 'What types of jobs are available?',
    a: 'We have positions in construction (framing, concrete, demolition, general labour), warehouse and logistics (pickers, packers, forklift operators), agriculture, transportation, and cold storage. Both temporary and permanent roles are available.',
  },
  {
    q: 'How and when do I get paid?',
    a: 'WorkNation pays workers on a regular schedule\u2014typically weekly. Pay rates vary by position, experience, and industry. Your specific pay rate and schedule are confirmed before you start any assignment.',
  },
  {
    q: 'Can I choose my own schedule?',
    a: 'Yes. You tell us when you want to work and how much. We offer flexible scheduling including day shifts, evening shifts, weekends, and on-call availability. Work as much or as little as you want.',
  },
  {
    q: 'Do I need experience to register?',
    a: 'Not necessarily. While some positions require specific certifications or experience, we have many entry-level general labour positions available. We\u2019ll match you with roles that fit your current skill level.',
  },
  {
    q: 'What should I bring to my first assignment?',
    a: 'Bring valid government-issued ID, appropriate work attire (steel-toed boots for construction/warehouse), and any relevant certifications. Specific requirements for each assignment will be communicated before your start date.',
  },
  {
    q: 'How do I get referred to permanent positions?',
    a: 'Many of our temporary assignments lead to permanent positions. Work diligently and demonstrate reliability, and our employer partners frequently offer permanent roles to strong performers. We actively advocate for our workers.',
  },
  {
    q: 'Is WorkNation a legitimate staffing agency?',
    a: 'Absolutely. WorkNation Labour Ltd is a fully licensed, WCB-compliant staffing agency based in Vancouver, British Columbia. We handle all payroll, taxes, and workplace insurance for our workers.',
  },
];

const FAQItem = ({ faq, isOpen, toggle }) => (
  <div className="border-b border-gray-200 last:border-b-0" data-testid="faq-item">
    <button
      onClick={toggle}
      className="w-full flex items-center justify-between py-5 px-4 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#003366] focus:ring-inset rounded"
      aria-expanded={isOpen}
      data-testid="faq-toggle"
    >
      <span className="text-lg font-semibold text-[#003366] pr-4">{faq.q}</span>
      <ChevronDown
        className={`flex-shrink-0 text-[#003366] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        size={22}
        aria-hidden="true"
      />
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
    >
      <p className="px-4 pb-5 text-gray-700 leading-relaxed">{faq.a}</p>
    </div>
  </div>
);

const FAQSection = ({ variant = 'full' }) => {
  const [openIndex, setOpenIndex] = useState({ employer: 0, jobseeker: 0 });

  const toggleEmployer = (i) => setOpenIndex((prev) => ({ ...prev, employer: prev.employer === i ? -1 : i }));
  const toggleJobSeeker = (i) => setOpenIndex((prev) => ({ ...prev, jobseeker: prev.jobseeker === i ? -1 : i }));

  return (
    <section className="py-20 px-6 bg-gray-50" data-testid="faq-section" id="faq">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-4">Frequently Asked Questions</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about WorkNation Labour Ltd&apos;s staffing services across British Columbia.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <h3 className="text-2xl font-bold text-[#003366] mb-6 flex items-center">
              <span className="w-2 h-8 bg-[#003366] rounded-full mr-3" aria-hidden="true"></span>
              For Employers
            </h3>
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              {employerFAQs.map((faq, i) => (
                <FAQItem key={i} faq={faq} isOpen={openIndex.employer === i} toggle={() => toggleEmployer(i)} />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-[#003366] mb-6 flex items-center">
              <span className="w-2 h-8 bg-[#003366] rounded-full mr-3" aria-hidden="true"></span>
              For Job Seekers
            </h3>
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              {jobSeekerFAQs.map((faq, i) => (
                <FAQItem key={i} faq={faq} isOpen={openIndex.jobseeker === i} toggle={() => toggleJobSeeker(i)} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
